import type {
  Camp,
  Partner,
  ItineraryDay,
  Activity,
  Quote,
  QuoteItem,
  PaymentMilestone,
  Participant,
  Task,
  Note,
  CampPartner,
  ExpenseActual,
  ExpenseEstimate,
  CampStatus,
} from '@/types/database.ts'
import { CAMP_STATUS_ORDER } from '@/lib/constants'
import type { AppAction } from './actions.ts'

export interface CampData {
  itineraryDays: ItineraryDay[]
  activities: Activity[]
  quotes: Quote[]
  quoteItems: QuoteItem[]
  paymentMilestones: PaymentMilestone[]
  participants: Participant[]
  tasks: Task[]
  notes: Note[]
  campPartners: CampPartner[]
  expenseActuals: ExpenseActual[]
  expenseEstimates: ExpenseEstimate[]
}

export interface AppState {
  camps: Camp[]
  partners: Partner[]
  campData: Record<string, CampData>
}

// 段は飛ばしてよい（問い合わせから一気に確定、精算中から戻す など）。同じ段への変更だけは何もしない。
const isKnownStatus = (s: CampStatus) => CAMP_STATUS_ORDER.includes(s)

function now(): string {
  return new Date().toISOString()
}

function uuid(): string {
  return crypto.randomUUID()
}

function emptyCampData(): CampData {
  return {
    itineraryDays: [],
    activities: [],
    quotes: [],
    quoteItems: [],
    paymentMilestones: [],
    participants: [],
    tasks: [],
    notes: [],
    campPartners: [],
    expenseActuals: [],
    expenseEstimates: [],
  }
}

function ensureTripData(state: AppState, campId: string): CampData {
  return state.campData[campId] ?? emptyCampData()
}

function recalcQuoteTotals(quotes: Quote[], items: QuoteItem[]): Quote[] {
  return quotes.map((q) => ({
    ...q,
    total: items.filter((qi) => qi.quote_id === q.id).reduce((sum, qi) => sum + qi.subtotal, 0),
  }))
}

export function appReducer(state: AppState, action: AppAction): AppState {
  const timestamp = now()

  switch (action.type) {
    // ==================== TRIPS ====================

    case 'ADD_CAMP': {
      const id = uuid()
      const newCamp: Camp = {
        ...action.payload,
        id,
        created_at: timestamp,
        updated_at: timestamp,
      }
      return {
        ...state,
        camps: [...state.camps, newCamp],
        campData: { ...state.campData, [id]: emptyCampData() },
      }
    }

    case 'UPDATE_CAMP': {
      const { id, ...updates } = action.payload
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === id ? { ...t, ...updates, updated_at: timestamp } : t,
        ),
      }
    }

    case 'DELETE_CAMP': {
      const { id } = action.payload
      const { [id]: _removed, ...remainingTripData } = state.campData
      return {
        ...state,
        camps: state.camps.filter((t) => t.id !== id),
        campData: remainingTripData,
      }
    }

    case 'CHANGE_CAMP_STATUS': {
      const { id, status: newStatus } = action.payload
      const camp = state.camps.find((t) => t.id === id)
      if (!camp) return state

      if (newStatus === camp.status || !isKnownStatus(newStatus)) return state

      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === id ? { ...t, status: newStatus, updated_at: timestamp } : t,
        ),
      }
    }

    case 'DUPLICATE_TRIP': {
      const { id } = action.payload
      const original = state.camps.find((t) => t.id === id)
      if (!original) return state

      const newTripId = uuid()
      const newCamp: Camp = {
        ...original,
        id: newTripId,
        name: `${original.name} (コピー)`,
        status: 'inquiry',
        progress: 0,
        budget_spent: 0,
        created_at: timestamp,
        updated_at: timestamp,
      }

      const originalData = ensureTripData(state, id)

      // Map old day IDs to new day IDs
      const dayIdMap = new Map<string, string>()
      const newDays = originalData.itineraryDays.map((day) => {
        const newDayId = uuid()
        dayIdMap.set(day.id, newDayId)
        return { ...day, id: newDayId, camp_id: newTripId }
      })

      const newActivities = originalData.activities.map((act) => ({
        ...act,
        id: uuid(),
        itinerary_day_id: dayIdMap.get(act.itinerary_day_id) ?? act.itinerary_day_id,
      }))

      // Map old quote IDs to new quote IDs
      const quoteIdMap = new Map<string, string>()
      const newQuotes = originalData.quotes.map((q) => {
        const newQuoteId = uuid()
        quoteIdMap.set(q.id, newQuoteId)
        return { ...q, id: newQuoteId, camp_id: newTripId, status: 'draft' as const, created_at: timestamp }
      })

      const newQuoteItems = originalData.quoteItems.map((qi) => ({
        ...qi,
        id: uuid(),
        quote_id: quoteIdMap.get(qi.quote_id) ?? qi.quote_id,
      }))

      const newMilestones = originalData.paymentMilestones.map((m) => ({
        ...m,
        id: uuid(),
        camp_id: newTripId,
        status: 'pending' as const,
        paid_at: null,
      }))

      const newParticipants = originalData.participants.map((p) => ({
        ...p,
        id: uuid(),
        camp_id: newTripId,
        status: 'interested' as const,
      }))

      const newTasks = originalData.tasks.map((t) => ({
        ...t,
        id: uuid(),
        camp_id: newTripId,
        status: 'todo' as const,
        created_at: timestamp,
      }))

      const newNotes = originalData.notes.map((n) => ({
        ...n,
        id: uuid(),
        camp_id: newTripId,
        created_at: timestamp,
      }))

      const newTripPartners = originalData.campPartners.map((tp) => ({
        ...tp,
        id: uuid(),
        camp_id: newTripId,
      }))

      const newExpenseActuals = originalData.expenseActuals.map((ea) => ({
        ...ea,
        id: uuid(),
        camp_id: newTripId,
        quote_item_id: null,
        created_at: timestamp,
      }))

      const newExpenseEstimates = originalData.expenseEstimates.map((ee) => ({
        ...ee,
        id: uuid(),
        camp_id: newTripId,
        created_at: timestamp,
      }))

      return {
        ...state,
        camps: [...state.camps, newCamp],
        campData: {
          ...state.campData,
          [newTripId]: {
            itineraryDays: newDays,
            activities: newActivities,
            quotes: newQuotes,
            quoteItems: newQuoteItems,
            paymentMilestones: newMilestones,
            participants: newParticipants,
            tasks: newTasks,
            notes: newNotes,
            campPartners: newTripPartners,
            expenseActuals: newExpenseActuals,
            expenseEstimates: newExpenseEstimates,
          },
        },
      }
    }

    // ==================== ITINERARY ====================

    // 旅程を触る操作では、案件の itinerary_updated_at も進める。サーバ側は
    // トリガーで同じ値を持つが、再取得するまでの間に画面が古い時刻を出すと
    // 「これ最新？」を消すための表示が嘘をつく。
    case 'ADD_ITINERARY_DAY': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const { id: providedId, ...rest } = action.payload
      const newDay: ItineraryDay = { ...rest, id: providedId ?? uuid() }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, itineraryDays: [...data.itineraryDays, newDay] },
        },
      }
    }

    case 'UPDATE_ITINERARY_DAY': {
      const { id, ...updates } = action.payload
      // Find which trip this day belongs to
      const campId = Object.keys(state.campData).find((tid) =>
        state.campData[tid].itineraryDays.some((d) => d.id === id),
      )
      if (!campId) return state
      const data = state.campData[campId]
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            itineraryDays: data.itineraryDays.map((d) =>
              d.id === id ? { ...d, ...updates } : d,
            ),
          },
        },
      }
    }

    case 'DELETE_ITINERARY_DAY': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      // Remove the day, then renumber remaining days sequentially (1, 2, 3...)
      const remaining = data.itineraryDays
        .filter((d) => d.id !== id)
        .sort((a, b) => a.day_number - b.day_number)
        .map((d, i) => ({ ...d, day_number: i + 1 }))
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            itineraryDays: remaining,
            activities: data.activities.filter((a) => a.itinerary_day_id !== id),
          },
        },
      }
    }

    case 'ADD_ACTIVITY': {
      // Find trip by itinerary day
      const dayId = action.payload.itinerary_day_id
      const campId = Object.keys(state.campData).find((tid) =>
        state.campData[tid].itineraryDays.some((d) => d.id === dayId),
      )
      if (!campId) return state
      const data = state.campData[campId]
      const { id: providedId, ...rest } = action.payload
      const newActivity: Activity = { ...rest, id: providedId ?? uuid() }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, activities: [...data.activities, newActivity] },
        },
      }
    }

    case 'UPDATE_ACTIVITY': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            activities: data.activities.map((a) =>
              a.id === id ? { ...a, ...updates } : a,
            ),
          },
        },
      }
    }

    case 'DELETE_ACTIVITY': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            activities: data.activities.filter((a) => a.id !== id),
          },
        },
      }
    }

    case 'REORDER_ACTIVITIES': {
      const { campId, itineraryDayId, activityIds } = action.payload
      const data = ensureTripData(state, campId)
      const reordered = data.activities.map((a) => {
        if (a.itinerary_day_id !== itineraryDayId) return a
        const newOrder = activityIds.indexOf(a.id)
        return newOrder >= 0 ? { ...a, sort_order: newOrder + 1 } : a
      })
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp, itinerary_updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, activities: reordered },
        },
      }
    }

    // ==================== QUOTES ====================

    case 'ADD_QUOTE': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newQuote: Quote = { ...action.payload, id: action.payload.id ?? uuid(), created_at: timestamp }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, quotes: [...data.quotes, newQuote] },
        },
      }
    }

    case 'UPDATE_QUOTE': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            quotes: data.quotes.map((q) =>
              q.id === id ? { ...q, ...updates } : q,
            ),
          },
        },
      }
    }

    case 'ADD_QUOTE_ITEM': {
      const { campId, ...itemPayload } = action.payload
      const data = ensureTripData(state, campId)
      const newItem: QuoteItem = { ...itemPayload, id: uuid() }
      const newItems = [...data.quoteItems, newItem]
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            quoteItems: newItems,
            quotes: recalcQuoteTotals(data.quotes, newItems),
          },
        },
      }
    }

    case 'UPDATE_QUOTE_ITEM': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      const newItems = data.quoteItems.map((qi) => qi.id === id ? { ...qi, ...updates } : qi)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            quoteItems: newItems,
            quotes: recalcQuoteTotals(data.quotes, newItems),
          },
        },
      }
    }

    case 'DELETE_QUOTE_ITEM': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      const newItems = data.quoteItems.filter((qi) => qi.id !== id)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            quoteItems: newItems,
            quotes: recalcQuoteTotals(data.quotes, newItems),
          },
        },
      }
    }

    case 'RECALCULATE_QUOTE_TOTAL': {
      const { quoteId, campId } = action.payload
      const data = ensureTripData(state, campId)
      const total = data.quoteItems
        .filter((qi) => qi.quote_id === quoteId)
        .reduce((sum, qi) => sum + qi.subtotal, 0)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            quotes: data.quotes.map((q) =>
              q.id === quoteId ? { ...q, total } : q,
            ),
          },
        },
      }
    }

    // ==================== PAYMENTS ====================

    case 'ADD_PAYMENT_MILESTONE': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newMilestone: PaymentMilestone = { ...action.payload, id: uuid() }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, paymentMilestones: [...data.paymentMilestones, newMilestone] },
        },
      }
    }

    case 'UPDATE_PAYMENT_MILESTONE': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            paymentMilestones: data.paymentMilestones.map((m) =>
              m.id === id ? { ...m, ...updates } : m,
            ),
          },
        },
      }
    }

    case 'MARK_PAYMENT_PAID': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            paymentMilestones: data.paymentMilestones.map((m) =>
              m.id === id ? { ...m, status: 'paid', paid_at: timestamp } : m,
            ),
          },
        },
      }
    }

    case 'DELETE_PAYMENT_MILESTONE': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            paymentMilestones: data.paymentMilestones.filter((m) => m.id !== id),
          },
        },
      }
    }

    // ==================== PARTICIPANTS ====================

    case 'ADD_PARTICIPANT': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newParticipant: Participant = { ...action.payload, id: uuid() }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, participants: [...data.participants, newParticipant] },
        },
      }
    }

    case 'UPDATE_PARTICIPANT': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            participants: data.participants.map((p) =>
              p.id === id ? { ...p, ...updates } : p,
            ),
          },
        },
      }
    }

    case 'DELETE_PARTICIPANT': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            participants: data.participants.filter((p) => p.id !== id),
          },
        },
      }
    }

    case 'UPDATE_PARTICIPANT_STATUS': {
      const { id, campId, status } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            participants: data.participants.map((p) =>
              p.id === id ? { ...p, status } : p,
            ),
          },
        },
      }
    }

    // ==================== TASKS ====================

    case 'ADD_TASK': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newTask: Task = { ...action.payload, id: uuid(), created_at: timestamp, updated_at: timestamp }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, tasks: [...data.tasks, newTask] },
        },
      }
    }

    case 'UPDATE_TASK': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            tasks: data.tasks.map((t) =>
              t.id === id ? { ...t, ...updates, updated_at: timestamp } : t,
            ),
          },
        },
      }
    }

    case 'DELETE_TASK': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            tasks: data.tasks.filter((t) => t.id !== id),
          },
        },
      }
    }

    case 'UPDATE_TASK_STATUS': {
      const { id, campId, status } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            tasks: data.tasks.map((t) =>
              t.id === id ? { ...t, status, updated_at: timestamp } : t,
            ),
          },
        },
      }
    }

    case 'BULK_UPDATE_TASK_STATUS': {
      const { ids, campId, status } = action.payload
      const data = ensureTripData(state, campId)
      const idSet = new Set(ids)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            tasks: data.tasks.map((t) =>
              idSet.has(t.id) ? { ...t, status, updated_at: timestamp } : t,
            ),
          },
        },
      }
    }

    // ==================== NOTES ====================

    case 'ADD_NOTE': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newNote: Note = { ...action.payload, id: uuid(), created_at: timestamp, updated_at: timestamp }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, notes: [...data.notes, newNote] },
        },
      }
    }

    case 'UPDATE_NOTE': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            notes: data.notes.map((n) =>
              n.id === id ? { ...n, ...updates, updated_at: timestamp } : n,
            ),
          },
        },
      }
    }

    case 'DELETE_NOTE': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            notes: data.notes.filter((n) => n.id !== id),
          },
        },
      }
    }

    // ==================== EXPENSE ACTUALS ====================

    case 'ADD_EXPENSE_ACTUAL': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newExpense: ExpenseActual = { ...action.payload, id: uuid(), created_at: timestamp }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, expenseActuals: [...data.expenseActuals, newExpense] },
        },
      }
    }

    case 'UPDATE_EXPENSE_ACTUAL': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            expenseActuals: data.expenseActuals.map((ea) =>
              ea.id === id ? { ...ea, ...updates } : ea,
            ),
          },
        },
      }
    }

    case 'DELETE_EXPENSE_ACTUAL': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            expenseActuals: data.expenseActuals.filter((ea) => ea.id !== id),
          },
        },
      }
    }

    // ==================== EXPENSE ESTIMATES ====================

    case 'ADD_EXPENSE_ESTIMATE': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newEstimate: ExpenseEstimate = { ...action.payload, id: uuid(), created_at: timestamp }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, expenseEstimates: [...data.expenseEstimates, newEstimate] },
        },
      }
    }

    case 'UPDATE_EXPENSE_ESTIMATE': {
      const { id, campId, ...updates } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            expenseEstimates: data.expenseEstimates.map((ee) =>
              ee.id === id ? { ...ee, ...updates } : ee,
            ),
          },
        },
      }
    }

    case 'DELETE_EXPENSE_ESTIMATE': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            expenseEstimates: data.expenseEstimates.filter((ee) => ee.id !== id),
          },
        },
      }
    }

    // ==================== PARTNERS ====================

    case 'ADD_PARTNER': {
      const newPartner: Partner = { ...action.payload, id: uuid(), created_at: timestamp }
      return {
        ...state,
        partners: [...state.partners, newPartner],
      }
    }

    case 'UPDATE_PARTNER': {
      const { id, ...updates } = action.payload
      return {
        ...state,
        partners: state.partners.map((p) =>
          p.id === id ? { ...p, ...updates } : p,
        ),
      }
    }

    case 'DELETE_PARTNER': {
      const { id } = action.payload
      return {
        ...state,
        partners: state.partners.filter((p) => p.id !== id),
      }
    }

    case 'ASSIGN_PARTNER_TO_TRIP': {
      const campId = action.payload.camp_id
      const data = ensureTripData(state, campId)
      const newTripPartner: CampPartner = { ...action.payload, id: uuid() }
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: { ...data, campPartners: [...data.campPartners, newTripPartner] },
        },
      }
    }

    case 'REMOVE_PARTNER_FROM_TRIP': {
      const { id, campId } = action.payload
      const data = ensureTripData(state, campId)
      return {
        ...state,
        camps: state.camps.map((t) =>
          t.id === campId ? { ...t, updated_at: timestamp } : t,
        ),
        campData: {
          ...state.campData,
          [campId]: {
            ...data,
            campPartners: data.campPartners.filter((tp) => tp.id !== id),
          },
        },
      }
    }

    // ==================== HYDRATE ====================

    case 'HYDRATE': {
      return action.payload
    }

    default:
      return state
  }
}
